import React from 'react';

interface Dot {
  x: number;
  y: number;
}

export interface GridShapeProps {
  rows: number;
  columns: number;
  strokeColor: string;
  strokeWidth: number;
  dots?: Dot[];
}

export const GridShape = ({
  rows,
  columns,
  strokeColor,
  strokeWidth,
  dots,
}: GridShapeProps) => {
  // Calculate the inner width and height taking stroke into account
  const innerWidth = 100 - strokeWidth;
  const innerHeight = 100 - strokeWidth;

  const getLines = () => {
    const lines = [];
    // Adjust step size to fit within the inner dimensions
    const rowStep = innerHeight / rows;
    const columnStep = innerWidth / columns;

    // Horizontal lines
    for (let i = 0; i <= rows; i++) {
      lines.push(
        <line
          key={`h-${i}`}
          x1={strokeWidth / 2}
          y1={i * rowStep + strokeWidth / 2}
          x2={innerWidth + strokeWidth / 2}
          y2={i * rowStep + strokeWidth / 2}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />,
      );
    }

    // Vertical lines
    for (let i = 0; i <= columns; i++) {
      lines.push(
        <line
          key={`v-${i}`}
          x1={i * columnStep + strokeWidth / 2}
          y1={strokeWidth / 2}
          x2={i * columnStep + strokeWidth / 2}
          y2={innerHeight + strokeWidth / 2}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />,
      );
    }

    return lines;
  };

  const getDots = () => {
    if (!dots) return null;

    const rowStep = innerHeight / rows;
    const columnStep = innerWidth / columns;

    return dots.map((dot, index) => (
      <circle
        key={`dot-${index}`}
        cx={dot.x * columnStep + strokeWidth / 2}
        cy={dot.y * rowStep + strokeWidth / 2}
        r={strokeWidth * 1.5} // Radius can be adjusted as needed
        fill={strokeColor}
      />
    ));
  };

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ width: '100%', height: '100%' }}
    >
      {/* Render the outline */}
      <rect
        x={strokeWidth / 2}
        y={strokeWidth / 2}
        width={innerWidth}
        height={innerHeight}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        fill="none"
      />
      {/* Render the grid lines inside the outline */}
      {getLines()}
      {/* Render the dots */}
      {getDots()}
    </svg>
  );
};
